import { supabase } from '../lib/supabase';

interface PicqerResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PicqerProduct {
  idproduct: number;
  productcode: string;
  name: string;
  price: number;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  barcode?: string;
  ean?: string;
  stock?: number;
  active: boolean;
  description?: string;
  type?: string; // Product type: normal, unlimited_stock, virtual_composition, composition_with_stock
}

interface ProductImportResult {
  picqerProducts: PicqerProduct[];
  matchedProducts: Array<{
    picqerProduct: PicqerProduct;
    existingProduct: Record<string, unknown>;
    matchType: 'ean' | 'productcode' | 'name';
  }>;
  newProducts: PicqerProduct[];
  totalFetched: number;
  filteredCount: number;
  alreadyLinkedCount: number;
}

interface PurchaseOrderData {
  purchaseOrderId: number;
  picqerReference: string;
  status: string;
}

interface ProductAnalysis {
  id: number;
  name: string;
  ean: string | null;
  pieces: number | null;
  cartons: number | null;
  hasEAN: boolean;
  hasCompanyProduct: boolean;
  hasPicqerProduct: boolean;
  picqerProductId: number | null;
  companyProductId: number | null;
  status: 'ready' | 'needs_picqer_link' | 'needs_company_product' | 'needs_ean';
  canInclude: boolean;
}

interface ProductAnalysisResponse {
  total: number;
  ready: number;
  needingSetup: number;
  products: ProductAnalysis[];
}

interface CreatePurchaseOrderResponse {
  success: boolean;
  data?: PurchaseOrderData & {
    bookingReference: string;
  };
  error?: string;
  productAnalysis?: ProductAnalysisResponse;
  canCreatePurchaseOrder?: boolean;
  nextSteps?: string[];
  existingPurchaseOrder?: {
    id: number;
    picqerId: number;
    reference: string;
  };
}

interface PicqerFulfilmentCustomer {
  idfulfilment_customer: number;
  name: string;
  code: string;
  address: string;
  zipcode: string;
  city: string;
  country: string;
  email: string;
  telephone: string;
  active: boolean;
}

export class PicqerService {
  private static async callEdgeFunction(action: string, payload: Record<string, unknown> = {}): Promise<PicqerResponse> {
    try {
      console.log('Calling edge function with:', { action, payload });
      
      const { data, error } = await supabase.functions.invoke('picqer-integration', {
        body: { action, ...payload },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        console.log('Error details:', {
          message: error.message,
          context: error.context,
          details: error.details,
          hint: error.hint,
          code: error.code,
          keys: Object.keys(error)
        });
        
        // Check if this is a FunctionsHttpError with a response body
        // This happens when the edge function returns a non-2xx status but with valid JSON
        if (error.context && error.context.body) {
          try {
            const errorData = typeof error.context.body === 'string' 
              ? JSON.parse(error.context.body) 
              : error.context.body;
            
            console.log('Parsed error response with body:', errorData);
            
            // If the response contains product analysis, return it instead of throwing
            if (errorData.productAnalysis) {
              return errorData;
            }
          } catch (parseError) {
            console.warn('Could not parse error response body:', parseError);
          }
        }
        
        // Also check if the error.details contains the response
        if (error.details) {
          try {
            const errorData = typeof error.details === 'string' 
              ? JSON.parse(error.details) 
              : error.details;
            
            console.log('Parsed error details:', errorData);
            
            // If the response contains product analysis, return it instead of throwing
            if (errorData.productAnalysis) {
              return errorData;
            }
          } catch (parseError) {
            console.warn('Could not parse error details:', parseError);
          }
        }
        
        throw error;
      }

      console.log('Edge function response:', data);
      return data;
    } catch (error) {
      console.error('Picqer service error:', error);
      return {
        success: false,
        error: (error as Error).message || 'Unknown error occurred'
      };
    }
  }



  static async testConnection(apiKey: string): Promise<PicqerResponse> {
    return this.callEdgeFunction('test-connection', { apiKey });
  }

  static async importProducts(apiKey: string, companyId: number): Promise<PicqerResponse<ProductImportResult>> {
    try {
      console.log('Calling dedicated product import edge function with:', { companyId });
      
      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: { apiKey, companyId },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Product import edge function response:', data);
      return data;
    } catch (error) {
      console.error('Picqer product import service error:', error);
      return {
        success: false,
        error: (error as Error).message || 'Unknown error occurred during product import'
      };
    }
  }

  static async importProduct(
    companyId: number,
    picqerProductId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get the company's Picqer API key
      const { data: company, error: companyError } = await supabase
        .from('company')
        .select('picqer_api_key')
        .eq('id', companyId)
        .single();

      if (companyError || !company?.picqer_api_key) {
        throw new Error('Picqer API key not found for this company');
      }

      // Get all products from Picqer and find the specific one
      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: {
          apiKey: company.picqer_api_key,
          companyId: companyId
        }
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      const response = data;

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch products from Picqer');
      }

      const importResult = response.data as {
        picqerProducts: PicqerProduct[];
        matchedProducts: Array<{
          picqerProduct: PicqerProduct;
          existingProduct: Record<string, unknown>;
          matchType: 'ean' | 'productcode' | 'name';
        }>;
        newProducts: PicqerProduct[];
        totalFetched: number;
        filteredCount: number;
        alreadyLinkedCount: number;
      };
      const product = importResult.picqerProducts.find(p => p.idproduct === picqerProductId);

      if (!product) {
        throw new Error('Product not found in Picqer');
      }

      // Create the company product with all available fields filled
      const productData = {
        company_id: companyId,
        name: product.name,
        // Use EAN if available, otherwise try barcode as fallback
        ean: product.ean ? parseInt(product.ean, 10) : 
             (product.barcode ? parseInt(product.barcode, 10) : null),
        hs_code: null, // HS code not available in Picqer
        pieces_per_carton: null, // Pieces per carton not available in Picqer
        weight_per_carton: null, // Weight in Picqer is per product, not per carton
        carton_length: null, // Dimensions in Picqer are per product, not per carton
        carton_width: null, // Dimensions in Picqer are per product, not per carton
        carton_height: null, // Dimensions in Picqer are per product, not per carton
        unit_price: product.price || null,
        picqer_product_id: product.idproduct,
      };

      const { error: saveError } = await supabase
        .from('company_product')
        .insert(productData);

      if (saveError) throw saveError;

      return {
        success: true
      };
    } catch (err: unknown) {
      console.error('Error importing single product:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to import product'
      };
    }
  }

  static async updateCompanyPicqerConnection(
    companyId: number,
    picqerData: {
      apiKey: string;
      fulfilmentCustomerId: number;
      enabled: boolean;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('company')
        .update({
          picqer_api_key: picqerData.apiKey,
          picqer_fulfilment_customer_id: picqerData.fulfilmentCustomerId,
          picqer_enabled: picqerData.enabled,
          wms_provider: picqerData.enabled ? 'picqer' : null,
        })
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating company Picqer connection:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to update company Picqer connection'
      };
    }
  }

  static async disconnectCompanyPicqer(companyId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('company')
        .update({
          picqer_api_key: null,
          picqer_fulfilment_customer_id: null,
          picqer_enabled: false,
          wms_provider: null,
        })
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error disconnecting company from Picqer:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to disconnect company from Picqer'
      };
    }
  }

  static async linkProduct(
    companyId: number,
    picqerProductId: number,
    existingProductId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('company_product')
        .update({ picqer_product_id: picqerProductId })
        .eq('id', existingProductId)
        .eq('company_id', companyId);

      if (error) throw error;

      return {
        success: true
      };
    } catch (err: unknown) {
      console.error('Error linking product:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to link product'
      };
    }
  }

  static async linkProducts(
    companyId: number,
    linkRequests: Array<{ existingProductId: number; picqerProductId: number }>
  ): Promise<{
    success: boolean;
    data?: { successCount: number; errorCount: number };
    errors?: string[];
    error?: string;
  }> {
    try {
      console.log('Linking products:', { companyId, linkCount: linkRequests.length });

      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: {
          action: 'link',
          companyId,
          linkRequests,
        },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Product linking response:', data);
      return data;
    } catch (error) {
      console.error('Error linking products:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to link products'
      };
    }
  }

  static async unlinkProduct(
    companyId: number,
    productId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('company_product')
        .update({ picqer_product_id: null })
        .eq('id', productId)
        .eq('company_id', companyId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: unknown) {
      console.error('Error unlinking product:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to unlink product'
      };
    }
  }

  static async getProductStock(companyId: number): Promise<{
    success: boolean;
    data?: Array<{
      productId: number;
      picqerProductId: number;
      stock: number;
      productName: string;
    }>;
    error?: string;
  }> {
    try {
      console.log('Fetching product stock from Picqer:', { companyId });

      const { data, error } = await supabase.functions.invoke('picqer-stock', {
        body: { companyId },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Product stock response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching product stock:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to fetch product stock'
      };
    }
  }

  /**
   * Analyze products for a booking without creating a purchase order
   * @param bookingId - The ID of the booking to analyze
   * @returns Promise with product analysis result
   */
  static async analyzeProductsForBooking(bookingId: number): Promise<{
    success: boolean;
    data?: ProductAnalysisResponse;
    error?: string;
  }> {
    try {
      console.log('Analyzing products for booking:', { bookingId });

      const { data, error } = await supabase.functions.invoke('picqer-purchase-orders', {
        body: { 
          bookingId,
          analyzeOnly: true // Flag to indicate we only want analysis, not creation
        },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        console.error('Error details:', {
          message: error.message,
          context: error.context,
          details: error.details,
          status: error.status,
          statusText: error.statusText
        });
        
        // Try to get the response body from the error
        let errorResponse = null;
        
        // Check if this is a FunctionsHttpError with a response body
        if (error.context && error.context.body) {
          try {
            let errorData;
            
            // Handle ReadableStream
            if (error.context.body instanceof ReadableStream) {
              console.log('Response body is a ReadableStream, attempting to read...');
              const reader = error.context.body.getReader();
              const chunks = [];
              
              try {
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  chunks.push(value);
                }
                
                const text = new TextDecoder().decode(new Uint8Array(chunks.flat()));
                console.log('Read stream text:', text);
                
                errorData = JSON.parse(text);
              } catch (streamError) {
                console.warn('Could not read stream:', streamError);
              }
            } else {
              errorData = typeof error.context.body === 'string' 
                ? JSON.parse(error.context.body) 
                : error.context.body;
            }
            
            console.log('Parsed error response with body:', errorData);
            errorResponse = errorData;
          } catch (parseError) {
            console.warn('Could not parse error response body:', parseError);
          }
        }
        
        // Also check if the error.details contains the response
        if (!errorResponse && error.details) {
          try {
            const errorData = typeof error.details === 'string' 
              ? JSON.parse(error.details) 
              : error.details;
            
            console.log('Parsed error details:', errorData);
            errorResponse = errorData;
          } catch (parseError) {
            console.warn('Could not parse error details:', parseError);
          }
        }
        
        // If we found a response with product analysis, return it
        if (errorResponse && errorResponse.productAnalysis) {
          console.log('Returning error response with product analysis:', errorResponse);
          return {
            success: true,
            data: errorResponse.productAnalysis
          };
        }
        
        // If we have any response data, return it
        if (errorResponse) {
          console.log('Returning error response:', errorResponse);
          return errorResponse;
        }
        
        throw error;
      }

      console.log('Product analysis response:', data);
      return {
        success: true,
        data: data.productAnalysis
      };
    } catch (error) {
      console.error('Error analyzing products:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to analyze products'
      };
    }
  }

  /**
   * Create a purchase order in Picqer for a storage booking
   * @param bookingId - The ID of the booking to create a purchase order for
   * @returns Promise with purchase order creation result
   */
  static async createPurchaseOrderForBooking(bookingId: number): Promise<CreatePurchaseOrderResponse> {
    try {
      console.log('Creating purchase order for booking:', { bookingId });

      let data, error;
      try {
        const result = await supabase.functions.invoke('picqer-purchase-orders', {
          body: { bookingId },
        });
        data = result.data;
        error = result.error;
        
        // IMPORTANT: Supabase may populate data even with non-2xx status codes
        // Always check data first, even if there's an error
        if (data) {
          console.log('Response data (regardless of error status):', data);
          // If data has success: false, return it directly
          if (data.success === false) {
            console.log('Edge function returned error in data:', data);
            return data;
          }
          // Success case
          if (data.success) {
            console.log('Purchase order creation response:', data);
            return data;
          }
        }
      } catch (invokeError) {
        console.error('Exception during function invoke:', invokeError);
        // If the invoke itself throws, wrap it
        error = invokeError as any;
        if (!error.message) {
          error.message = String(invokeError);
        }
      }

      if (error) {
        console.error('Supabase function invocation error:', error);
        console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
        console.error('Error details:', {
          message: error.message,
          context: error.context,
          details: error.details,
          status: error.status,
          statusText: error.statusText,
          name: error.name,
          stack: error.stack
        });
        
        // Try to get the response body from the error
        let errorResponse = null;
        let errorMessage = error.message || 'Failed to create purchase order';
        
        // FIRST: Check if error has a data property (sometimes Supabase puts response here)
        if ((error as any).data) {
          console.log('Error has data property, using it:', (error as any).data);
          errorResponse = (error as any).data;
          if (errorResponse && errorResponse.error) {
            errorMessage = errorResponse.error;
            return errorResponse; // Return immediately if we have the full response
          }
        }
        
        // Log all possible error properties for debugging
        console.log('Error object keys:', Object.keys(error));
        if ((error as any).response) {
          console.log('Error has response property:', (error as any).response);
          // Try to extract from response
          try {
            const responseData = (error as any).response;
            if (typeof responseData === 'string') {
              const parsed = JSON.parse(responseData);
              if (parsed && parsed.error) {
                errorMessage = parsed.error;
                errorResponse = parsed;
              }
            } else if (responseData && responseData.error) {
              errorMessage = responseData.error;
              errorResponse = responseData;
            }
          } catch (e) {
            console.warn('Could not parse error.response:', e);
          }
        }
        
        // Check if this is a FunctionsHttpError with a response body
        if (error.context && error.context.body) {
          try {
            let errorData;
            
            // Handle ReadableStream
            if (error.context.body instanceof ReadableStream) {
              console.log('Response body is a ReadableStream, attempting to read...');
              const reader = error.context.body.getReader();
              const chunks = [];
              
              try {
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  chunks.push(value);
                }
                
                const text = new TextDecoder().decode(new Uint8Array(chunks.flat()));
                console.log('Read stream text:', text);
                
                errorData = JSON.parse(text);
              } catch (streamError) {
                console.warn('Could not read stream:', streamError);
              }
            } else {
              errorData = typeof error.context.body === 'string' 
                ? JSON.parse(error.context.body) 
                : error.context.body;
            }
            
            console.log('Parsed error response with body:', errorData);
            errorResponse = errorData;
            
            // Extract error message from response
            if (errorData && errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (parseError) {
            console.warn('Could not parse error response body:', parseError);
          }
        }
        
        // Also check if the error.details contains the response
        if (!errorResponse && error.details) {
          try {
            const errorData = typeof error.details === 'string' 
              ? JSON.parse(error.details) 
              : error.details;
            
            console.log('Parsed error details:', errorData);
            errorResponse = errorData;
            
            // Extract error message from details
            if (errorData && errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (parseError) {
            console.warn('Could not parse error details:', parseError);
          }
        }
        
        // Check if error.message contains JSON (sometimes Supabase includes response in message)
        if (error.message && !errorResponse) {
          // Try to extract JSON from the message
          const jsonMatch = error.message.match(/\{.*\}/s);
          if (jsonMatch) {
            try {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData && errorData.error) {
                errorMessage = errorData.error;
                errorResponse = errorData;
              }
            } catch (parseError) {
              // Not valid JSON, continue
            }
          }
          
          // If message itself looks like it contains the error details, use it
          if (error.message.includes('Picqer Purchase Order API error')) {
            errorMessage = error.message;
          }
        }
        
        // LAST RESORT: If we still don't have a good error message and errorResponse,
        // try to construct from what we have
        if (!errorResponse && errorMessage === 'Failed to create purchase order') {
          // Check if we can get more info from the error object itself
          const errorString = JSON.stringify(error, null, 2);
          console.log('Full error as string:', errorString);
          
          // Try to find any JSON in the error string
          const allJsonMatches = errorString.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
          if (allJsonMatches) {
            for (const jsonStr of allJsonMatches) {
              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed && parsed.error && typeof parsed.error === 'string') {
                  errorMessage = parsed.error;
                  errorResponse = parsed;
                  break;
                }
              } catch (e) {
                // Continue to next match
              }
            }
          }
        }
        
        // If we found a response with product analysis, return it
        if (errorResponse && errorResponse.productAnalysis) {
          console.log('Returning error response with product analysis:', errorResponse);
          return {
            ...errorResponse,
            error: errorMessage
          };
        }
        
        // If we have errorResponse (which should have success: false and error), return it directly
        if (errorResponse && typeof errorResponse === 'object' && 'success' in errorResponse) {
          console.log('Returning error response object:', errorResponse);
          return errorResponse;
        }
        
        // If we still don't have a good error message, try to construct one from available info
        if (errorMessage === 'Failed to create purchase order' || errorMessage.includes('non-2xx')) {
          // Try to build a more informative error message
          const parts: string[] = [];
          if (error.status) parts.push(`Status: ${error.status}`);
          if (error.statusText) parts.push(`Status Text: ${error.statusText}`);
          if (error.message && !error.message.includes('non-2xx')) {
            parts.push(`Message: ${error.message}`);
          }
          if (errorResponse && errorResponse.error) {
            parts.push(`API Error: ${errorResponse.error}`);
          }
          
          if (parts.length > 0) {
            errorMessage = parts.join(' | ');
          } else {
            // Last resort: show the full error object as string
            errorMessage = `Error: ${JSON.stringify({
              message: error.message,
              status: error.status,
              statusText: error.statusText,
              details: error.details,
              context: error.context
            }, null, 2)}`;
          }
        }
        
        // Return error response with extracted message
        return {
          success: false,
          error: errorMessage
        };
      }

      console.log('Purchase order creation response:', data);
      return data;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to create purchase order'
      };
    }
  }

  /**
   * Get purchase orders for a specific booking
   * @param bookingId - The ID of the booking
   * @returns Promise with purchase order data
   */
  static async getPurchaseOrdersForBooking(bookingId: number): Promise<{
    success: boolean;
    data?: Array<{
      id: number;
      picqer_purchase_order_id: number | null;
      picqer_purchaseorderid?: string | null;
      picqer_reference: string | null;
      booking_reference: string | null;
      status: string;
      supplier_name?: string;
      created_at: string;
      channeldock_delivery_id?: number | null;
      channeldock_reference?: string | null;
    }>;
    error?: string;
  }> {
    try {
      console.log('Fetching purchase orders for booking:', { bookingId });

      const { data, error } = await supabase
        .from('booking_purchase_orders')
        .select('id, picqer_purchase_order_id, picqer_purchaseorderid, picqer_reference, booking_reference, status, supplier_name, created_at, channeldock_delivery_id, channeldock_reference')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to fetch purchase orders'
      };
    }
  }

  /**
   * Check if a booking has any purchase orders
   * @param bookingId - The ID of the booking
   * @returns Promise with boolean indicating if purchase orders exist
   */
  static async hasBookingPurchaseOrders(bookingId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('booking_purchase_orders')
        .select('id')
        .eq('booking_id', bookingId)
        .limit(1);

      if (error) {
        throw error;
      }

      return (data || []).length > 0;
    } catch (error) {
      console.error('Error checking booking purchase orders:', error);
      return false;
    }
  }

  /**
   * Get fulfillment customers from Picqer
   * Uses the general Picqer API key configured in the edge function
   * @returns Promise with fulfillment customers list
   */
  static async getFulfilmentCustomers(): Promise<PicqerResponse<PicqerFulfilmentCustomer[]>> {
    return this.callEdgeFunction('get-fulfilment-customers', {}) as Promise<PicqerResponse<PicqerFulfilmentCustomer[]>>;
  }

  /**
   * Delete a purchase order and its associated products
   * @param purchaseOrderId - The ID of the purchase order to delete
   * @returns Promise with success status and count of deleted products
   */
  static async deletePurchaseOrder(purchaseOrderId: number): Promise<{ success: boolean; deletedProductsCount?: number; error?: string }> {
    try {
      console.log('Deleting purchase order:', { purchaseOrderId });

      // First count and delete the associated products
      const { data: products, error: countError } = await supabase
        .from('booking_purchase_order_products')
        .select('id')
        .eq('purchase_order_id', purchaseOrderId);

      if (countError) {
        console.error('Error counting purchase order products:', countError);
        throw countError;
      }

      const deletedProductsCount = products?.length || 0;
      console.log('Found products to delete:', deletedProductsCount);

      // Delete the associated products
      const { error: productsError } = await supabase
        .from('booking_purchase_order_products')
        .delete()
        .eq('purchase_order_id', purchaseOrderId);

      if (productsError) {
        console.error('Error deleting purchase order products:', productsError);
        throw productsError;
      }

      console.log('Successfully deleted products');

      // Then delete the purchase order
      const { error: orderError } = await supabase
        .from('booking_purchase_orders')
        .delete()
        .eq('id', purchaseOrderId);

      if (orderError) {
        console.error('Error deleting purchase order:', orderError);
        throw orderError;
      }

      console.log('Successfully deleted purchase order');
      return { success: true, deletedProductsCount };
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to delete purchase order'
      };
    }
  }

  static async createCompanyProduct(product: {
    company_id: number;
    name: string;
    ean: number;
    hs_code: number;
    pieces_per_carton: number;
    weight_per_carton: number;
    carton_length: number;
    carton_width: number;
    carton_height: number;
    unit_price: number;
    picqer_product_id: number;
  }): Promise<{ success: boolean; data?: Record<string, unknown>[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('company_product')
        .insert(product)
        .select();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating company product:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create company product'
      };
    }
  }

  /**
   * Create a product in Picqer and link it to a company product
   * @param companyId - The company ID
   * @param productData - Product data to create in Picqer
   * @returns Promise with creation result
   */
  static async createProductInPicqer(
    companyId: number,
    productData: {
      name: string;
      productcode: string;
      barcode?: string;
      price?: number;
      description?: string;
      active?: boolean;
    }
  ): Promise<{
    success: boolean;
    data?: {
      picqerProductId: number;
      companyProductId: number;
    };
    error?: string;
  }> {
    try {
      console.log('Creating product in Picqer:', { companyId, productData });

      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: {
          action: 'create-product',
          companyId,
          productData,
        },
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Product creation response:', data);
      return data;
    } catch (error) {
      console.error('Error creating product in Picqer:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to create product in Picqer'
      };
    }
  }

  /**
   * Create products in Picqer for booking products that don't exist yet
   * @param bookingId - The booking ID
   * @param productIds - Array of booking product IDs to create
   * @returns Promise with creation results
   */
  static async createMissingProductsForBooking(
    bookingId: number,
    productIds: number[]
  ): Promise<{
    success: boolean;
    data?: {
      createdCount: number;
      errorCount: number;
      results: Array<{
        productId: number;
        success: boolean;
        picqerProductId?: number;
        companyProductId?: number;
        error?: string;
      }>;
    };
    error?: string;
  }> {
    try {
      console.log('Creating missing products for booking:', { bookingId, productIds });

      const requestBody = {
        action: 'create-missing-products',
        bookingId,
        productIds,
      };

      console.log('Sending request to edge function:', requestBody);

      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: requestBody,
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Missing products creation response:', data);
      return data;
    } catch (error) {
      console.error('Error creating missing products:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to create missing products'
      };
    }
  }

  /**
   * Search for existing Picqer products and create company products for them
   * @param bookingId - The booking ID
   * @param productIds - Array of booking product IDs to search and link
   * @returns Promise with linking results
   */
  static async searchAndLinkExistingProducts(
    bookingId: number,
    productIds: number[]
  ): Promise<{
    success: boolean;
    data?: {
      createdCount: number;
      errorCount: number;
      results: Array<{
        productId: number;
        success: boolean;
        picqerProductId?: number;
        companyProductId?: number;
        error?: string;
      }>;
    };
    error?: string;
  }> {
    try {
      console.log('Searching and linking existing products for booking:', { bookingId, productIds });

      const requestBody = {
        action: 'search-and-link-existing-products',
        bookingId,
        productIds,
      };

      console.log('Sending request to edge function:', requestBody);

      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: requestBody,
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      console.log('Search and link response:', data);
      return data;
    } catch (error) {
      console.error('Error searching and linking products:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to search and link products'
      };
    }
  }
}

export type { PicqerProduct, ProductImportResult, PurchaseOrderData, CreatePurchaseOrderResponse, PicqerFulfilmentCustomer }; 